import { graphql } from 'react-relay';
import { useCallback, useEffect, useRef } from 'react';
import set from 'lodash/set';
import { NetworkStatus } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { PlatformSiteAri } from '@atlassian/ari';

import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getIdFromConfluenceContentAri } from '@confluence/content-types-utils/entry-points/getIdFromConfluenceContentAri.ts';
import { ContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';

import type { LocalActivity } from '../useLocalActivity.ts';
import { useLocalActivity } from '../useLocalActivity.ts';
import type { MergeResult } from '../MergeFunction.ts';

import type {
	confluence_useMyVisitsLocalActivityMyVisitsQuery as useMyVisitsLocalActivityMyVisitsQuery$data,
	confluence_useMyVisitsLocalActivityMyVisitsQueryVariables as useMyVisitsLocalActivityMyVisitsQueryVariables,
	confluence_useMyVisitsLocalActivityMyVisitsQuery_activities_Activities_myActivities_MyActivities_viewed_ActivitiesConnection_edges_ActivityEdge as MyVisitsEdge,
} from './__types__/confluence_useMyVisitsLocalActivityMyVisitsQuery.ts';
import type {
	useMyVisitsLocalActivityMyVisitsQueryVariables as useMyVisitsLocalActivityMyVisitsQueryV3Variables,
	useMyVisitsLocalActivityMyVisitsQuery as useMyVisitsLocalActivityMyVisitsQueryV3$data,
	useMyVisitsLocalActivityMyVisitsQuery_activity_Activity_myActivity_MyActivity_all_ActivityConnection_edges_ActivityItemEdge as MyVisitsEdgeV3,
} from './__types__/useMyVisitsLocalActivityMyVisitsQuery.ts';
import { mergeMyVisits } from './mergeMyVisits.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const useMyVisitsLocalActivityMyVisitsQueryV3 = graphql`
	query useMyVisitsLocalActivityMyVisitsQuery(
		$cloudId: ID!
		$first: Int!
		$after: String
		$spaceId: ID!
		$objectTypes: [String!]
		$eventTypes: [String!]
	) {
		activity {
			myActivity {
				all(
					filter: {
						type: AND
						rootContainerIds: [$cloudId]
						arguments: {
							products: ["CONFLUENCE"]
							objectTypes: $objectTypes
							eventTypes: $eventTypes
							containerIds: [$spaceId]
						}
					}
					first: $first
					after: $after
				) {
					edges {
						node {
							id
							object {
								id
								data {
									... on ConfluencePage {
										id
										title
										space {
											id
											key
										}
										metadata {
											titleEmojiDraft {
												value
											}
											titleEmojiPublished {
												value
											}
										}
									}
								}
							}
							event {
								id
								timestamp
								eventType
							}
						}
						cursor
					}
					pageInfo {
						hasNextPage
					}
				}
			}
		}
	}
`;
const EVENT_EXPIRATION_SECONDS = 60;

export type UseMyVisitsOptions = {
	spaceKey: string;
	pageSize?: number;
	skip?: boolean;
	fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only';
};

export type MyVisitsData = {
	edges: MyVisitsEdge[];
	hasNextPage: boolean;
};

const useMerge = (
	remoteEdges: MyVisitsEdge[],
	localActivity: LocalActivity,
	shouldMerge: boolean,
): Partial<MergeResult<MyVisitsEdge>> => {
	if (!shouldMerge) {
		return {};
	}
	return mergeMyVisits(remoteEdges, localActivity, {
		expirationSeconds: EVENT_EXPIRATION_SECONDS,
	});
};

const useRefetch = (
	apolloRefetch: () => void,
	loading: boolean,
	remoteEdges: MyVisitsEdge[],
	localActivity: LocalActivity,
	consistentContentIds?: Set<string>,
	latestExpiration?: Date,
) => {
	const didRefetch = useRef(false);
	const prevLatestExpiration = useRef<Date | undefined>();

	const refetch = useCallback(() => {
		didRefetch.current = true;
		apolloRefetch();
	}, [apolloRefetch]);

	// Analytics & mark events as consistent when backend catches up
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (didRefetch.current && !loading && consistentContentIds) {
			didRefetch.current = false;
			consistentContentIds.forEach((contentId: string) => {
				localActivity.markAsConsistent(contentId);
			});

			const localActivityCount = localActivity.viewedEvents.length;
			const newlyConsistentCount = consistentContentIds.size;
			const inconsistencyCount = localActivityCount - consistentContentIds.size;
			const activityPlatformCount = remoteEdges.length;
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					actionSubject: 'useMyVisits',
					action: 'refetched',
					source: 'spaceViews',
					attributes: {
						localActivityCount,
						newlyConsistentCount,
						inconsistencyCount,
						activityPlatformCount,
					},
				},
			}).fire();
		}
	});

	// Refetch on event expiration
	useEffect(() => {
		if (
			latestExpiration &&
			(!prevLatestExpiration.current || latestExpiration > prevLatestExpiration.current)
		) {
			prevLatestExpiration.current = latestExpiration;
			refetch();
		}
	});

	return refetch;
};

const transformActivityPlatformLegacyUgcRemovalMitigationResponseV3 = (
	activitiesData: useMyVisitsLocalActivityMyVisitsQueryV3$data | undefined,
): useMyVisitsLocalActivityMyVisitsQuery$data | undefined => {
	if (!activitiesData?.activity?.myActivity?.all?.edges) {
		return undefined;
	}

	const edges = activitiesData.activity.myActivity.all.edges;
	const transformedEdges = edges.map((edge: MyVisitsEdgeV3) => {
		const { node } = edge;
		// @ts-expect-error __typename is not present in Relay generated types but present in the response
		const pageData = node?.object?.data?.__typename === 'ConfluencePage' ? node.object.data : null;
		const metadata = pageData?.metadata;

		const nodeId =
			getIdFromConfluenceContentAri({
				ari: pageData?.id || '',
				resourceType: ContentType.PAGE,
			}) || '';

		const emojiProperties = [
			{
				id: 'emoji-title-published',
				key: 'emoji-title-published',
				value: metadata?.titleEmojiPublished?.value,
				version: null,
			},
			{
				id: 'emoji-title-draft',
				key: 'emoji-title-draft',
				value: metadata?.titleEmojiDraft?.value,
				version: null,
			},
		].filter((prop) => prop.value);

		return {
			cursor: edge.cursor,
			node: {
				id: nodeId,
				timestamp: node?.event?.timestamp ? String(node.event.timestamp) : null,
				object: {
					id: node.object.id,
					localResourceId: nodeId,
					name: pageData?.title,
					content: pageData && {
						id: nodeId,
						title: pageData.title,
						space: pageData.space,
						properties: emojiProperties.length > 0 ? { nodes: emojiProperties } : null,
					},
				},
			},
		};
	});

	return {
		activities: {
			myActivities: {
				viewed: {
					edges: transformedEdges,
					pageInfo: activitiesData.activity.myActivity.all.pageInfo,
				},
			},
		},
	} as useMyVisitsLocalActivityMyVisitsQuery$data;
};

const useQueryActivityPlatform = ({
	skip,
	pageSize = 25,
	spaceKey,
	fetchPolicy = 'cache-first',
}: UseMyVisitsOptions) => {
	const { cloudId } = useSessionData();
	const { data, loading, fetchMore, error, refetch, networkStatus } = useQueryInterop<
		useMyVisitsLocalActivityMyVisitsQuery$data | useMyVisitsLocalActivityMyVisitsQueryV3$data,
		| useMyVisitsLocalActivityMyVisitsQueryVariables
		| useMyVisitsLocalActivityMyVisitsQueryV3Variables
	>(useMyVisitsLocalActivityMyVisitsQueryV3, {
		skip,
		variables: {
			first: pageSize,
			spaceId: getSpaceAriV3(spaceKey, cloudId),
			after: '',
			cloudId: PlatformSiteAri.create({ siteId: cloudId }).toString(),
			objectTypes: ['PAGE'],
			eventTypes: ['VIEWED'],
		},
		fetchPolicy,
		notifyOnNetworkStatusChange: true,
		client: getAGGClient(),
	});

	return {
		data: transformActivityPlatformLegacyUgcRemovalMitigationResponseV3(
			data as useMyVisitsLocalActivityMyVisitsQueryV3$data | undefined,
		),
		loading,
		fetchMore,
		error,
		refetch,
		networkStatus,
	};
};

/**
 * This hook:
 *   1) Queries Activity Platform
 *   2) Queries Local Activity
 *   3) Merges the results of 1 & 2
 * It will also refetch after a local event "expires" and will mark
 * any newly-consistent content IDs as such.
 */
export const useMyVisits = (options: UseMyVisitsOptions) => {
	const { spaceKey, skip } = options;

	const {
		data: remoteData,
		loading,
		fetchMore,
		error,
		refetch: apolloRefetch,
		networkStatus,
	} = useQueryActivityPlatform(options);

	const remoteEdges = (remoteData?.activities?.myActivities?.viewed?.edges || []).filter(
		inSpaceAndNotNull(spaceKey),
	);

	const hasNextPage =
		Boolean(remoteData?.activities?.myActivities?.viewed?.pageInfo.hasNextPage) &&
		remoteEdges.length > 0;
	const after = remoteEdges[remoteEdges.length - 1]?.cursor;

	const nextPage = useCallback(async () => {
		if (!hasNextPage || !after) {
			return;
		}
		await fetchMore({
			variables: {
				after,
			},
			updateQuery: (
				prevResult:
					| useMyVisitsLocalActivityMyVisitsQuery$data
					| useMyVisitsLocalActivityMyVisitsQueryV3$data,
				{ fetchMoreResult },
			) => {
				if (!fetchMoreResult) {
					return prevResult;
				}

				const result = { ...fetchMoreResult };

				const prevEdges =
					(prevResult as useMyVisitsLocalActivityMyVisitsQueryV3$data)?.activity?.myActivity?.all
						?.edges || [];
				const fetchMoreEdges =
					(fetchMoreResult as useMyVisitsLocalActivityMyVisitsQueryV3$data)?.activity?.myActivity
						?.all?.edges || [];

				set(result, 'activity.myActivity.all.edges', [...prevEdges, ...fetchMoreEdges]);

				return result;
			},
		});
	}, [fetchMore, hasNextPage, after]);

	const localActivity = useLocalActivity({
		skip,
		filters: { spaceKey },
	});

	const shouldMerge = !skip && networkStatus !== NetworkStatus.loading && Boolean(remoteData);
	const { mergedNodes, latestExpiration, consistentContentIds } = useMerge(
		remoteEdges,
		localActivity,
		shouldMerge,
	);
	const data: MyVisitsData | undefined = mergedNodes && {
		edges: mergedNodes,
		hasNextPage,
	};

	const refetch = useRefetch(
		apolloRefetch,
		loading,
		remoteEdges,
		localActivity,
		consistentContentIds,
		latestExpiration,
	);

	return {
		data,
		loading,
		error,
		nextPage,
		refetch,
	};
};

const inSpaceAndNotNull =
	(spaceKey: string) =>
	(edge: MyVisitsEdge | null): edge is MyVisitsEdge =>
		Boolean(edge && edge.node?.object?.content?.space?.key === spaceKey);

const getSpaceAriV3: (spaceKey: string, cloudId: string) => string = (spaceKey, cloudId) => {
	const hexEncodedSpace = Buffer.from(`v1/${cloudId}/${spaceKey}`, 'ascii').toString('hex');
	return `ari:cloud:confluence:${cloudId}:space/${hexEncodedSpace}`;
};
